import { useEffect, useState } from 'react'
import { Link as RouterLink, useSearchParams } from 'react-router-dom'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import ListSubheader from '@mui/material/ListSubheader'
import Button from '@mui/material/Button'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableRow from '@mui/material/TableRow'
import TableCell from '@mui/material/TableCell'

import useOrganizations, { orgLabel } from '../models/useOrganizations'
import useAudit, { type AuditEntry } from '../models/useAudit'
import { useOrgRole } from '../hooks/useOrgRole'
import BackListItem from './BackListItem'
import UserLabel from './UserLabel'
import useOrgMembers, { type OrgMember } from '../models/useOrgMembers'
import IconCopyButton from './IconCopyButton'
import {
  AUDIT_ACTION_GROUPS,
  groupAuditActions,
  auditActionLabel,
  auditActor,
  auditActorLabel,
  auditSupportMarker,
  auditTargetCell,
  matchesAuditSearch,
} from '../utilities/auditLog'

// The target cell: the name (a link when the target has a page), the raw `type: id` in a tooltip,
// and a copy button for the id. A row from a router before router#1551 has no name and shows
// `type: id` as before.
function AuditTarget({ entry }: { entry: AuditEntry }) {
  const cell = auditTargetCell(entry)
  const text = cell.href ? (
    <Link component={RouterLink} to={cell.href} variant="body2" sx={{ whiteSpace: 'nowrap' }}>
      {cell.text}
    </Link>
  ) : (
    <Typography variant={cell.tooltip ? 'body2' : 'caption'} sx={{ color: cell.tooltip ? 'text.primary' : 'text.secondary' }}>
      {cell.text}
    </Typography>
  )
  return (
    <Stack direction="row" sx={{ alignItems: 'center' }}>
      {cell.tooltip ? (
        <Tooltip title={cell.tooltip} placement="top" arrow>
          <span>{text}</span>
        </Tooltip>
      ) : (
        text
      )}
      {entry.targetId && <IconCopyButton value={entry.targetId} aria-label="Copy target id" />}
    </Stack>
  )
}

// The actor cell: the person as the app shows every person (app#401). The name comes from the org's
// member list, which the page reads with the org (useOrganizations). No actor: the router did it.
// A noBGP support action (app#421) gets a "noBGP support" chip after the person. The tooltip says
// what allowed the action: the organization setting or temporary access.
function ActorCell({ entry, roster }: { entry: AuditEntry; roster: OrgMember[] }) {
  const actor = auditActor(entry, roster)
  const person = actor ? <UserLabel user={actor} /> : <Typography variant="body2">{auditActorLabel(entry)}</Typography>
  const support = auditSupportMarker(entry)
  if (!support) return person
  return (
    <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
      {person}
      {/* tabIndex: the tooltip also opens on keyboard focus. describeChild: a screen reader reads
          "noBGP support", then the tooltip as a description, not in place of the label. */}
      <Tooltip title={support.tooltip} placement="top" arrow describeChild>
        <Chip label={support.label} size="small" variant="outlined" color="warning" tabIndex={0} />
      </Tooltip>
    </Stack>
  )
}

// Drawer page (under /account) — not a modal. Reached from the Account page's
// "Audit log" link, or from a node page's History link with `?q=<node id>`; the
// drawer opens because the path includes /account.
export default function Audit() {
  const { activeOrgId, organizations } = useOrganizations()
  const { entries, loaded, hasMore, searchUnsupported, load, actions: orgActions, loadActions } = useAudit()
  const members = useOrgMembers(state => state.members)
  const { can } = useOrgRole()
  const [searchParams] = useSearchParams()
  const qParam = searchParams.get('q') ?? ''

  const [q, setQ] = useState(qParam)
  const [action, setAction] = useState('')
  const canView = can('members.manage')
  const activeOrg = organizations.find(o => o.id === activeOrgId)

  // A second History link opens this page again with a new `q`: take it.
  useEffect(() => setQ(qParam), [qParam])

  // Reload when the active org, the search text or the action changes (lightly debounced).
  useEffect(() => {
    if (!activeOrgId || !canView) return
    const t = setTimeout(() => void load(activeOrgId, { q: q.trim(), action }), 250)
    return () => clearTimeout(t)
  }, [activeOrgId, q, action, canView, load])

  // The menu: once per org, the org's own actions (router#1561).
  useEffect(() => {
    if (!activeOrgId || !canView) return
    void loadActions(activeOrgId)
  }, [activeOrgId, canView, loadActions])

  // The org's own actions when the router sent them; else the static list. ⚠ The selected action
  // stays in the menu even when the list does not hold it, so the select never has a value with
  // no option.
  const actionGroups = orgActions
    ? groupAuditActions(action && !orgActions.includes(action) ? [...orgActions, action] : orgActions)
    : AUDIT_ACTION_GROUPS

  // ⚠⚠ THE ACTION IS FILTERED AGAIN HERE, AND IT IS NOT BELT-AND-BRACES. From router#1551 the
  // router matches `action` as a SUBSTRING (`a.action ILIKE '%…%'`, `src/db/audit.go`), where it
  // used to be equality. The menu offers ONE EXACT action, so on that router `billing.credit`
  // also returns `billing.credit_buy`, `billing.credit_status`, `billing.credit_auto_reload` and
  // the rest; `member.invite` returns revocations; `node.command` returns
  // `node.command_subscribe`. A reader filtering "Member invited" is then shown revocations
  // under a heading that says invitations.
  //
  // ⚠ There is NO REFUSAL to detect here — `action` is an old field, so the semantics changed
  // under a name that still works. Keeping only the exact action is a no-op against the older
  // equality router, so this is safe in all three states the table below describes.
  //
  // ⚠ `action` is still SENT. It narrows the page server-side, so paging stays cheap; this only
  // drops the rows the widened match added.
  const shown = entries.filter(
    e => (!action || e.action === action) && (!searchUnsupported || matchesAuditSearch(e, q, members))
  )
  const filtered = !!q.trim() || !!action

  return (
    <Stack sx={{
      gap: 3
    }}>
      <BackListItem to="/account">Audit log</BackListItem>
      {!canView ? (
        <Typography variant="body2" sx={{
          color: 'text.secondary'
        }}>
          Only organization owners and admins can view the audit log.
        </Typography>
      ) : (
        <>
          <Typography variant="subtitle2" sx={{
            color: 'text.secondary'
          }}>
            {activeOrg ? orgLabel(activeOrg) : ''}
          </Typography>

          <Stack direction={{ xs: 'column', sm: 'row' }} sx={{ gap: 2 }}>
            <TextField
              size="small"
              label="Search"
              placeholder="Node name, node id, person, email or action"
              value={q}
              onChange={e => setQ(e.target.value)}
              sx={{ flex: 2 }}
            />
            <TextField
              select
              size="small"
              label="Action"
              value={action}
              onChange={e => setAction(e.target.value)}
              // displayEmpty: without it the empty value draws a blank box, not "All actions".
              slotProps={{ select: { displayEmpty: true } }}
              sx={{ flex: 1, minWidth: 180 }}
            >
              <MenuItem value="">All actions</MenuItem>
              {actionGroups.map(group => [
                <ListSubheader key={`group-${group.group}`}>{group.group}</ListSubheader>,
                group.actions.map(a => (
                  <MenuItem key={a} value={a}>
                    {auditActionLabel({ action: a })}
                  </MenuItem>
                )),
              ])}
            </TextField>
          </Stack>

          {searchUnsupported && (
            <Typography variant="caption" sx={{
              color: 'text.secondary'
            }}>
              The server cannot search the audit log yet. This list shows only the loaded events that match.
            </Typography>
          )}

          {loaded && shown.length === 0 ? (
            <Typography variant="body2" sx={{
              color: 'text.disabled'
            }}>
              {filtered ? 'No audit events match.' : 'No audit events yet.'}
            </Typography>
          ) : (
            <Box sx={{ overflowX: 'auto' }}>
              {/* minWidth + horizontal scroll: the drawer's Overflow clips X, so a
                  narrow drawer scrolls the table instead of cutting off columns.
                  No cell wraps: a wrapped email or label breaks mid-word and cannot be read.
                  The time cell holds two lines on purpose: the date, then the time. */}
              <Table size="small" sx={{ minWidth: 560, '& th, & td': { whiteSpace: 'nowrap' } }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Time</TableCell>
                    <TableCell>Actor</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Target</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {shown.map(e => (
                    <TableRow key={e.id}>
                      <TableCell>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {new Date(e.createdAt).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" sx={{ display: 'block', color: 'text.secondary' }}>
                          {new Date(e.createdAt).toLocaleTimeString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <ActorCell entry={e} roster={members} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={e.action} placement="top" arrow>
                          <Typography variant="body2" component="span">
                            {auditActionLabel(e)}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <AuditTarget entry={e} />
                      </TableCell>
                      <TableCell>
                        {e.ok ? (
                          <Chip label="ok" size="small" variant="outlined" color="success" />
                        ) : (
                          <Chip label={e.code || 'failed'} size="small" variant="outlined" color="error" />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}

          {hasMore && (
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => activeOrgId && load(activeOrgId, { q: q.trim(), action, append: true })}
            >
              Load more
            </Button>
          )}
        </>
      )}
    </Stack>
  )
}
